/**
 * Wistia embeds add some JSON-LD to the page that can contain links to specific offsets
 * into the video (if there are chapters defined).
 *
 * This has proven problematic when used with a channel, as it leads to a combinatoric
 * explosion of possible URLs, given multiple offset times for each video on the channel.
 *
 * These URLs then get crawled by search engines, resulting in a lot of unnecessary
 * server requests and a lot of spam data in Google Search Console.
 *
 * Here, we watch the page for newly-inserted <script> tags, and if one contains
 * Wistia JSON-LD, then we remove the deep links.
 */
export function editWistiaJsonLd() {
  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach(node => {
          if (isScriptElement(node) && node.type === 'application/ld+json') {
            stripJsonLd(node);
          }
        });
      }
    });
  });

  observer.observe(document.head, { childList: true, subtree: false });
}

function stripJsonLd(node: HTMLScriptElement) {
  try {
    const jsonLd = JSON.parse(node.textContent ?? '');

    if (!jsonLd['@id'].includes('wistia')) return;

    delete jsonLd.hasPart;
    delete jsonLd.potentialAction;

    node.textContent = JSON.stringify(jsonLd);

    console.log(`Stripped Wistia JSON-LD in script ${node.id}`);
  } catch (error) {
    console.error(`Error stripping JSON-LD from script ${node.id}`, error);
  }
}

const isScriptElement = (node: Node): node is HTMLScriptElement => node.nodeName === 'SCRIPT';
